import React, { useMemo, useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, MoreHorizontal } from 'lucide-react';
import { AuditCardexPanel } from '../admin/AuditCardexPanel';
import {
  Activity,
  AlertTriangle,
  BarChart3,
  CheckCircle2,
  Clock,
  Database,
  Download,
  RefreshCw,
  Search,
  ShieldCheck,
  Target,
  Users,
  Sliders,
  HelpCircle,
  Info
} from 'lucide-react';
import { APP_VERSION } from '../../lib/shared-logic';
import { displayOperationalMonth, indexToOperationalMonth } from '../../lib/operational-control/operational-start-period-config';
import { useOperationalControlCenter } from '../../lib/operational-control/useOperationalControlCenter';
import { loadOperationalSettings } from '../../lib/operational-control/operational-control-settings';
import { classifyOperationalLoadError } from '../../lib/operational-control/executive-summary-model';
import type { OperationalInstanceRow, OperationalStatus } from '../../lib/operational-control/types';
import ExecutiveOperationalSummary from './ExecutiveOperationalSummary';

const cn = (...classes: Array<string | false | null | undefined>) => classes.filter(Boolean).join(' ');

const statusTone: Record<OperationalStatus, string> = {
  AL_DIA: 'bg-emerald-500/10 text-emerald-300 border-emerald-500/20',
  ATENCION: 'bg-amber-500/10 text-amber-300 border-amber-500/20',
  CRITICO: 'bg-red-500/10 text-red-300 border-red-500/20',
  ONBOARDING: 'bg-sky-500/10 text-sky-300 border-sky-500/20',
  NOT_STARTED: 'bg-slate-500/10 text-slate-300 border-slate-500/20',
  SIN_DATOS: 'bg-zinc-500/10 text-zinc-300 border-zinc-500/20',
  SIN_OBLIGACION: 'bg-zinc-500/5 text-zinc-400 border-zinc-500/10',
  SIN_CONFIGURACION: 'bg-zinc-500/5 text-zinc-400 border-zinc-500/10',
  SIN_META_VENCIDA: 'bg-sky-500/10 text-sky-300 border-sky-500/20'
};

const statusLabel: Record<OperationalStatus, string> = {
  AL_DIA: 'Al dia',
  ATENCION: 'Atencion',
  CRITICO: 'Critico',
  ONBOARDING: 'Onboarding',
  NOT_STARTED: 'No iniciado',
  SIN_DATOS: 'Sin datos',
  SIN_OBLIGACION: 'Sin Obligación',
  SIN_CONFIGURACION: 'Sin config',
  SIN_META_VENCIDA: 'Sin meta vencida'
};

const getScoreColorClass = (score: number | null) => {
  if (score === null) return 'text-zinc-500 font-normal';
  if (score >= 90) return 'text-emerald-400 font-black';
  if (score >= 75) return 'text-amber-400 font-black';
  if (score >= 60) return 'text-orange-400 font-black';
  return 'text-red-400 font-black';
};

const StatCard = ({
  icon: Icon,
  label,
  value,
  detail,
  tone = 'emerald'
}: {
  icon: any;
  label: string;
  value: string | number;
  detail: string;
  tone?: 'emerald' | 'amber' | 'red' | 'sky' | 'slate';
}) => {
  const toneClasses = {
    emerald: 'text-emerald-300 bg-emerald-500/10 border-emerald-500/20',
    amber: 'text-amber-300 bg-amber-500/10 border-amber-500/20',
    red: 'text-red-300 bg-red-500/10 border-red-500/20',
    sky: 'text-sky-300 bg-sky-500/10 border-sky-500/20',
    slate: 'text-slate-300 bg-slate-500/10 border-slate-500/20'
  }[tone];

  return (
    <div className="rounded-lg border border-slate-800 bg-slate-950/70 p-4 min-h-[124px]">
      <div className="flex items-start justify-between gap-3">
        <div className={cn('w-9 h-9 rounded-lg border flex items-center justify-center shrink-0', toneClasses)}>
          <Icon size={18} />
        </div>
        <span className="text-[10px] font-black uppercase tracking-widest text-slate-500 text-right leading-tight">{label}</span>
      </div>
      <div className="mt-4 text-3xl font-black text-white leading-none">{value}</div>
      <div className="mt-2 text-[11px] font-bold text-slate-500 leading-snug">{detail}</div>
    </div>
  );
};

const StatusBadge = ({ status }: { status: OperationalStatus }) => (
  <span className={cn('inline-flex items-center rounded-md border px-2 py-1 text-[10px] font-black uppercase tracking-widest', statusTone[status])}>
    {statusLabel[status]}
  </span>
);

const pct = (value: number) => `${Math.round(value)}%`;

const buildModulePath = (module: 'planning' | 'tracking', row: Pick<OperationalInstanceRow, 'instanceId' | 'vpId'>) => {
  const params = new URLSearchParams();
  params.set('instId', row.instanceId);
  if (row.vpId) params.set('vpId', row.vpId);
  return `/${module}?${params.toString()}`;
};

const exportCsv = (rows: OperationalInstanceRow[]) => {
  const headers = [
    'instancia',
    'vp',
    'estado_operativo',
    'score',
    'plan_pct',
    'kpi_coverage',
    'captura_actual',
    'inicio_operativo',
    'inicio_source',
    'lifecycle',
    'onboarding',
    'stale_operativo',
    'alertas'
  ];
  const body = rows.map((row) => [
    row.instanceName,
    row.vpName,
    row.operationalStatus,
    row.operationalScore ?? '',
    row.planPct,
    row.kpiGoalCoverage,
    row.currentMonthCapture ? 'SI' : 'NO',
    displayOperationalMonth(row.operationalStartMonth),
    row.operationalStartSource,
    row.lifecycleSource || '',
    row.onboardingState,
    row.staleMonthsOperational,
    row.alerts.map((alert) => alert.code).join('|')
  ]);
  const csv = [headers, ...body]
    .map((line) => line.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    .join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = 'control-operativo-instancias.csv';
  anchor.click();
  URL.revokeObjectURL(url);
};

const InstanceMobileCard = ({ row, onOpenPlan, onOpenTracking, onOpenCardex }: { row: OperationalInstanceRow, onOpenPlan: () => void, onOpenTracking: () => void, onOpenCardex: () => void }) => (
  <div className="rounded-lg border border-slate-800 bg-slate-950 p-4 space-y-4">
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <h3 className="text-sm font-black text-white leading-tight">{row.instanceName}</h3>
        <p className="mt-1 text-[10px] font-bold uppercase tracking-widest text-slate-500">{row.vpName}</p>
      </div>
      <StatusBadge status={row.operationalStatus} />
    </div>
    <div className="grid grid-cols-2 gap-3 text-xs">
      <div>
        <div className="text-[9px] font-black uppercase tracking-widest text-slate-500">Score</div>
        <div className={cn('mt-1 text-sm font-black', getScoreColorClass(row.operationalScore))}>
          {row.operationalScore === null ? 'N/A' : pct(row.operationalScore)}
        </div>
      </div>
      <div>
        <div className="text-[9px] font-black uppercase tracking-widest text-slate-500">Inicio</div>
        <div className="mt-1 font-black text-white">{displayOperationalMonth(row.operationalStartMonth)}</div>
      </div>
      <div>
        <div className="text-[9px] font-black uppercase tracking-widest text-slate-500">Plan</div>
        <div className="mt-1 font-black text-white">{pct(row.planPct)}</div>
      </div>
      <div>
        <div className="text-[9px] font-black uppercase tracking-widest text-slate-500">KPI meta</div>
        <div className="mt-1 font-black text-white">{pct(row.kpiGoalCoverage)}</div>
      </div>
    </div>
    <div className="flex flex-wrap gap-2">
      {row.operationalStatus === 'SIN_OBLIGACION' || row.operationalStatus === 'SIN_CONFIGURACION' || row.operationalStatus === 'SIN_META_VENCIDA' ? (
        <span className="rounded-md border border-zinc-800 bg-zinc-900/50 px-2 py-1 text-[10px] font-black uppercase text-zinc-500">
          Exento de Captura
        </span>
      ) : (
        <span className={cn('rounded-md border px-2 py-1 text-[10px] font-black uppercase', row.currentMonthCapture ? 'border-emerald-500/20 text-emerald-300 bg-emerald-500/10' : 'border-amber-500/20 text-amber-300 bg-amber-500/10')}>
          {row.currentMonthCapture ? 'Captura actual' : 'Sin captura'}
        </span>
      )}
      <span className="rounded-md border border-slate-700 bg-slate-900 px-2 py-1 text-[10px] font-black uppercase text-slate-300">
        Stale {row.staleMonthsOperational}
      </span>
      <span className="rounded-md border border-slate-700 bg-slate-900 px-2 py-1 text-[10px] font-black uppercase text-slate-300">
        {row.onboardingState}
      </span>
    </div>
    <div className="mt-3 flex gap-2 border-t border-slate-800 pt-3">
      <button onClick={onOpenPlan} className="flex-1 px-2 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded text-[10px] font-black uppercase tracking-widest transition-colors text-center">Plan</button>
      <button onClick={onOpenTracking} className="flex-1 px-2 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded text-[10px] font-black uppercase tracking-widest transition-colors text-center">KPI</button>
      <button onClick={onOpenCardex} className="flex-1 px-2 py-1.5 bg-slate-800 hover:bg-slate-700 text-emerald-400 rounded text-[10px] font-black uppercase tracking-widest transition-colors text-center">Cardex</button>
    </div>
  </div>
);

const GovernanceOperationalControlCenter = ({ user, selectedMonth }: { user: any; selectedMonth: string }) => {
  const navigate = useNavigate();
  const [activeView, setActiveView] = useState<'EXECUTIVE' | 'DETAIL'>('EXECUTIVE');
  const [analysisMonth, setAnalysisMonth] = useState(selectedMonth);
  const [selectedCardexRow, setSelectedCardexRow] = useState<any>(null);
  const [isCardexOpen, setIsCardexOpen] = useState(false);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const menuContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => setAnalysisMonth(selectedMonth), [selectedMonth]);

  // Cerrar menú al hacer clic fuera
  useEffect(() => {
    if (!openMenuId) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (menuContainerRef.current && !menuContainerRef.current.contains(e.target as Node)) {
        setOpenMenuId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openMenuId]);

  const {
    data,
    loading,
    error,
    filters,
    setFilters,
    refresh,
    filteredRows,
    vpOptions
  } = useOperationalControlCenter(user, analysisMonth, 2026);

  const opSettings = useMemo(() => loadOperationalSettings(), []);

  const visibleCriticalAlerts = useMemo(
    () => (data?.alerts || []).filter((alert) => alert.severity === 'critical').slice(0, 5),
    [data]
  );

  if (loading) {
    return (
      <div className="h-full bg-black text-white flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <RefreshCw className="animate-spin text-emerald-400" size={28} />
          <span className="text-[11px] font-black uppercase tracking-widest text-slate-500">Cargando Control Operativo</span>
        </div>
      </div>
    );
  }

  if (error || !data) {
    const loadState = classifyOperationalLoadError(error || 'Error remoto sin datos');
    const errorTitle = loadState === 'SESSION_EXPIRED'
      ? 'Sesion expirada'
      : loadState === 'NO_SCOPE'
        ? 'Sin alcance operativo'
        : 'Error remoto de Control Operativo';
    return (
      <div className="h-full bg-black text-white flex items-center justify-center p-6">
        <div className="max-w-lg rounded-lg border border-red-500/20 bg-red-500/10 p-6 text-center">
          <AlertTriangle className="mx-auto text-red-300" size={32} />
          <h2 className="mt-4 text-lg font-black uppercase tracking-tight">{errorTitle}</h2>
          <p className="mt-2 text-sm text-red-100/70">{error || 'Sin datos operativos.'}</p>
          <p className="mt-2 text-[10px] font-black uppercase tracking-widest text-red-200/50">El error no se representa como cero, N/A ni tabla vacia.</p>
          <button onClick={refresh} className="mt-5 rounded-lg bg-red-500 px-4 py-2 text-xs font-black uppercase tracking-widest text-white">
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  const summary = data.summary;

  return (
    <div className="h-full overflow-y-auto bg-black text-white">
      <div className="mx-auto max-w-[1500px] px-4 py-5 lg:px-8 lg:py-7 space-y-6">
        <nav className="flex flex-wrap gap-2 rounded-xl border border-slate-800 bg-slate-950 p-2" aria-label="Vistas de Control Operativo">
          <button onClick={() => setActiveView('EXECUTIVE')} className={cn('rounded-lg px-5 py-3 text-[10px] font-black uppercase tracking-widest transition-colors', activeView === 'EXECUTIVE' ? 'bg-emerald-500 text-black' : 'text-slate-400 hover:bg-slate-900 hover:text-white')}>Resumen Ejecutivo</button>
          <button onClick={() => setActiveView('DETAIL')} className={cn('rounded-lg px-5 py-3 text-[10px] font-black uppercase tracking-widest transition-colors', activeView === 'DETAIL' ? 'bg-emerald-500 text-black' : 'text-slate-400 hover:bg-slate-900 hover:text-white')}>Detalle Operativo</button>
        </nav>

        {activeView === 'EXECUTIVE' ? (
          <ExecutiveOperationalSummary
            data={data}
            user={user}
            selectedMonth={analysisMonth}
            onSelectedMonthChange={setAnalysisMonth}
            onRefresh={refresh}
          />
        ) : (
        <>
        <header className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-md border border-emerald-500/20 bg-emerald-500/10 px-2 py-1 text-[10px] font-black uppercase tracking-widest text-emerald-300">
                Read-only observer
              </span>
              <span className="rounded-md border border-slate-800 bg-slate-950 px-2 py-1 text-[10px] font-black uppercase tracking-widest text-slate-500">
                {APP_VERSION}
              </span>
            </div>
            <h1 className="mt-3 text-2xl font-black uppercase tracking-tight text-white lg:text-4xl">Control Operativo</h1>
            <p className="mt-2 text-sm font-semibold text-slate-500">
              Salud operativa institucional, captura, onboarding y rezagos sin modificar motores canonicos.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => exportCsv(filteredRows)}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-800 bg-slate-950 px-4 py-3 text-[11px] font-black uppercase tracking-widest text-slate-300 hover:border-emerald-500/30 hover:text-emerald-300"
            >
              <Download size={14} /> CSV
            </button>
            <button
              onClick={refresh}
              className="inline-flex items-center gap-2 rounded-lg bg-emerald-500 px-4 py-3 text-[11px] font-black uppercase tracking-widest text-black hover:bg-emerald-400"
            >
              <RefreshCw size={14} /> Actualizar
            </button>
          </div>
        </header>

        <div className="rounded-lg border border-sky-500/20 bg-sky-500/5 p-4 space-y-2">
          <div className="text-[10px] font-black uppercase tracking-widest text-sky-300 flex items-center gap-2">
            <Info size={12} /> Breakdown Matemático del Score Operativo
          </div>
          <div className="text-xs font-bold text-sky-200/80 space-y-1 ml-4">
            <div className="font-mono text-[11px] leading-relaxed">
              <span className="text-amber-300">Score</span> = ( <span className="text-cyan-300">KPI_Compliance</span> × {opSettings.kpiWeight}% ) + ( <span className="text-lime-300">Plan_Progress</span> × {opSettings.planWeight}% ) - <span className="text-red-400">Stale_Penalty</span>
            </div>
            <div className="text-[10px] text-slate-400 mt-2 pl-2 border-l border-slate-700">
              Ejemplo: (85% × {opSettings.kpiWeight}%) + (90% × {opSettings.planWeight}%) - 10% = Score Final
            </div>
          </div>
        </div>

        <div className="rounded-lg border border-cyan-500/20 bg-cyan-500/5 p-4 space-y-3">
          <div className="flex items-start gap-3">
            <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-cyan-500/20 border border-cyan-500/30 shrink-0 mt-0.5">
              <HelpCircle size={14} className="text-cyan-300" />
            </div>
            <div className="flex-1 space-y-2">
              <h3 className="text-xs font-black uppercase tracking-widest text-cyan-300">Modelo de Activación Operativa Híbrido</h3>
              <div className="text-xs font-semibold text-cyan-100/70 space-y-1">
                <div>• <strong>SIN META = SIN OBLIGACIÓN</strong> — Las instancias sin metas configuradas NO generan rezago operativo ni penalización de disciplina.</div>
                <div>• <strong>Ventana Operativa</strong> — La disciplina operativa SOLO se evalúa durante la ventana operativa efectiva de cada instancia.</div>
                <div>• <strong>Stale Operativo</strong> — Solo se contabiliza después de la fecha de inicio operativo dentro de la ventana activa.</div>
                <div>• <strong>Onboarding Protegido</strong> — Las instancias en onboarding NO se penalizan como críticas independientemente del stale.</div>
              </div>
            </div>
          </div>
        </div>

        <section className="grid gap-3 sm:grid-cols-2 xl:grid-cols-7">
          <StatCard icon={Activity} label="Salud operativa" value={pct(summary.healthScore)} detail={`${summary.totalInstances} instancias visibles`} />
          <StatCard icon={AlertTriangle} label="Instancias criticas" value={summary.criticalInstances} detail="Solo dentro de ventana activa" tone={summary.criticalInstances > 0 ? 'red' : 'emerald'} />
          <StatCard icon={Clock} label="Captura mes actual" value={pct(summary.currentCapturePct)} detail={`${summary.currentCaptureCount} con captura`} tone="sky" />
          <StatCard icon={CheckCircle2} label="Planes completos" value={summary.completePlans} detail="PlanPct oficial live totals" tone="emerald" />
          <StatCard icon={Target} label="KPIs con meta" value={pct(summary.kpisWithGoalPct)} detail="Cobertura de metas configuradas" tone="slate" />
          <StatCard icon={Users} label="Onboarding" value={summary.onboardingInstances} detail="No penalizadas como rojas" tone="sky" />
          <StatCard icon={BarChart3} label="Rezagos" value={summary.staleInstances} detail="Stale operativo 2+" tone={summary.staleInstances > 0 ? 'amber' : 'emerald'} />
        </section>

        <div className="rounded-xl border border-slate-800 bg-slate-950/40 p-4 text-[10px] font-black uppercase tracking-wider flex flex-wrap gap-4 items-center justify-between text-slate-400 animate-in fade-in duration-500">
          <div className="flex flex-wrap gap-3 items-center">
            <span className="text-slate-500 font-bold uppercase tracking-widest text-[9px]">Métricas y Leyenda:</span>
            <span className="flex items-center gap-1 border border-emerald-500/20 bg-emerald-500/10 px-1.5 py-0.5 rounded text-emerald-300">🟢 ÓPTIMO (≥{opSettings.greenThreshold})</span>
            <span className="flex items-center gap-1 border border-amber-500/20 bg-amber-500/10 px-1.5 py-0.5 rounded text-amber-300">🟡 ESTABLE ({opSettings.yellowThreshold}-{opSettings.greenThreshold - 1})</span>
            <span className="flex items-center gap-1 border border-orange-500/20 bg-orange-500/10 px-1.5 py-0.5 rounded text-orange-300">🟠 RIESGO ({opSettings.orangeThreshold}-{opSettings.yellowThreshold - 1})</span>
            <span className="flex items-center gap-1 border border-red-500/20 bg-red-500/10 px-1.5 py-0.5 rounded text-red-300">🔴 CRÍTICO (&lt;{opSettings.orangeThreshold})</span>
            <span className="flex items-center gap-1 border border-zinc-700 bg-zinc-800/40 px-1.5 py-0.5 rounded text-zinc-500">⚪ SIN OBLIGACIÓN</span>
            <span className="flex items-center gap-1 border border-sky-500/20 bg-sky-500/10 px-1.5 py-0.5 rounded text-sky-300">🔵 SIN META VENCIDA</span>
          </div>
          <div className="flex items-center gap-2">
            <Info size={12} className="text-sky-300 shrink-0 animate-pulse" />
            <span className="normal-case text-slate-500 font-semibold tracking-normal"><strong>Stale:</strong> Meses consecutivos con meta &gt; 0 sin captura válida. <strong>SIN META = SIN OBLIGACIÓN:</strong> Las instancias sin metas configuradas NO generan rezago ni penalización operativa.</span>
          </div>
        </div>

        <section className="rounded-lg border border-slate-800 bg-slate-950/70 p-3">
          <div className="grid gap-3 lg:grid-cols-[1.2fr_1fr_1fr_1fr_auto]">
            <label className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
              <input
                value={filters.search}
                onChange={(event) => setFilters({ search: event.target.value })}
                placeholder="Buscar instancia o VP"
                className="h-11 w-full rounded-lg border border-slate-800 bg-black pl-9 pr-3 text-sm font-bold text-white outline-none focus:border-emerald-500/50"
              />
            </label>
            <select
              value={filters.vpId}
              onChange={(event) => setFilters({ vpId: event.target.value })}
              className="h-11 rounded-lg border border-slate-800 bg-black px-3 text-xs font-black uppercase tracking-widest text-white outline-none focus:border-emerald-500/50"
            >
              <option value="ALL">Todas las VP</option>
              {vpOptions.map((vp) => (
                <option key={vp.id} value={vp.id}>{vp.name}</option>
              ))}
            </select>
            <select
              value={filters.status}
              onChange={(event) => setFilters({ status: event.target.value })}
              className="h-11 rounded-lg border border-slate-800 bg-black px-3 text-xs font-black uppercase tracking-widest text-white outline-none focus:border-emerald-500/50"
            >
              <option value="ALL">Todos los estados</option>
              {Object.entries(statusLabel).map(([key, label]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>
            <select
              value={filters.filter}
              onChange={(event) => setFilters({ filter: event.target.value as any })}
              className="h-11 rounded-lg border border-slate-800 bg-black px-3 text-xs font-black uppercase tracking-widest text-white outline-none focus:border-emerald-500/50"
            >
              <option value="all">Sin filtro operativo</option>
              <option value="critical">Criticas</option>
              <option value="onboarding">Onboarding</option>
              <option value="noCurrentCapture">Sin captura</option>
              <option value="stale2">Stale 2+</option>
              <option value="stale3">Stale 3+</option>
              <option value="kpisWithoutGoal">KPIs sin meta</option>
              <option value="incompletePlans">Planes incompletos</option>
            </select>
            <div className="flex items-center justify-center rounded-lg border border-slate-800 bg-black px-3 text-[10px] font-black uppercase tracking-widest text-slate-500">
              {filteredRows.length} filas
            </div>
          </div>
        </section>

        <section className="grid gap-6 xl:grid-cols-[1fr_320px]">
          <div className="hidden overflow-hidden rounded-lg border border-slate-800 bg-slate-950 lg:block">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[1200px] border-collapse text-left">
                <thead className="bg-slate-900/80 text-[10px] font-black uppercase tracking-widest text-slate-500">
                  <tr>
                    <th className="px-4 py-3">Instancia</th>
                    <th className="px-4 py-3">VP</th>
                    <th className="px-4 py-3">Estado</th>
                    <th className="px-4 py-3">Score</th>
                    <th className="px-4 py-3">Plan %</th>
                    <th className="px-4 py-3">KPI coverage</th>
                    <th className="px-4 py-3">Captura actual</th>
                    <th className="px-4 py-3">Inicio operativo</th>
                    <th className="px-4 py-3">Lifecycle</th>
                    <th className="px-4 py-3">Onboarding</th>
                    <th className="px-4 py-3 relative group">
                      <div className="flex items-center gap-1 cursor-help">
                        <span>Stale</span>
                        <HelpCircle size={10} className="text-slate-500" />
                      </div>
                      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 w-52 bg-slate-950 border border-slate-800 rounded-xl p-2.5 shadow-2xl opacity-0 group-hover:opacity-100 pointer-events-none transition-all duration-300 z-50 text-[9px] leading-relaxed text-slate-400 font-medium normal-case tracking-normal">
                        <span className="font-black text-white block uppercase tracking-wider text-[9px] mb-1">¿Qué es el Stale?</span>
                        Meses consecutivos en la ventana operativa de la instancia sin registrar ninguna actividad válida (Capturas / Checklists).
                      </div>
                    </th>
                    <th className="px-4 py-3">Alertas</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-900">
                  {filteredRows.map((row) => (
                    <tr key={row.instanceId} className="hover:bg-slate-900/40">
                      <td className="px-4 py-4 align-top">
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <div className="max-w-[220px] text-sm font-black text-white leading-tight">{row.instanceName}</div>
                            <div className="mt-1 text-[10px] font-black uppercase tracking-widest text-slate-600">{row.instanceType}</div>
                          </div>
                          {/* Menú estado-React: sin hover-CSS, click siempre registrado */}
                          <div className="relative" ref={openMenuId === row.instanceId ? menuContainerRef : undefined}>
                            <button
                              className="flex items-center justify-center w-6 h-6 rounded-md bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white transition-colors border border-slate-700"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setOpenMenuId(openMenuId === row.instanceId ? null : row.instanceId);
                              }}
                            >
                              <MoreHorizontal size={14} />
                            </button>
                            {openMenuId === row.instanceId && (
                              <div className="absolute left-0 top-full mt-1 w-24 bg-slate-800 border border-slate-700 rounded-lg shadow-2xl z-[60] overflow-hidden flex flex-col animate-in fade-in zoom-in-95 duration-100">
                                <button
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setOpenMenuId(null);
                                    navigate(buildModulePath('planning', row));
                                  }}
                                  className="px-3 py-2 text-left text-[9px] font-black uppercase tracking-widest text-slate-300 hover:bg-slate-700 hover:text-white"
                                >
                                  Plan
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setOpenMenuId(null);
                                    navigate(buildModulePath('tracking', row));
                                  }}
                                  className="px-3 py-2 text-left text-[9px] font-black uppercase tracking-widest text-slate-300 hover:bg-slate-700 hover:text-white border-t border-slate-700/50"
                                >
                                  KPI
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setOpenMenuId(null);
                                    setSelectedCardexRow(row);
                                    setIsCardexOpen(true);
                                  }}
                                  className="px-3 py-2 text-left text-[9px] font-black uppercase tracking-widest text-emerald-400 hover:bg-slate-700 hover:text-emerald-300 border-t border-slate-700/50"
                                >
                                  Cardex
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4 align-top text-xs font-bold text-slate-300">{row.vpName}</td>
                      <td className="px-4 py-4 align-top"><StatusBadge status={row.operationalStatus} /></td>
                      <td className="px-4 py-4 align-top relative group">
                        {row.operationalStatus === 'SIN_META_VENCIDA' ? (
                          <>
                            <div className="text-sm font-black text-sky-400">N/A</div>
                            <div className="mt-1 text-[9px] text-sky-500 uppercase font-black tracking-wider leading-none">NO PENALIZA</div>
                            <div className="absolute left-1/2 bottom-full -translate-x-1/2 mb-2 w-64 bg-slate-950 border border-slate-800 rounded-xl p-3 shadow-2xl opacity-0 group-hover:opacity-100 pointer-events-none transition-all duration-300 z-50 text-[10px] leading-relaxed normal-case tracking-normal">
                              <div className="font-black text-white border-b border-slate-800 pb-1 uppercase tracking-wider mb-2 text-[9px]">Sin Obligación Activa</div>
                              <div className="text-slate-400">Existen metas futuras, pero ninguna meta vencida al mes de análisis.</div>
                              <div className="font-bold text-sky-400 mt-2">Próxima meta: {row.firstKpiMonthIndex !== null ? displayOperationalMonth(indexToOperationalMonth(row.firstKpiMonthIndex)) : 'N/A'}</div>
                            </div>
                          </>
                        ) : row.operationalStatus === 'SIN_CONFIGURACION' || row.operationalStatus === 'SIN_OBLIGACION' ? (
                           <div className="text-sm font-black text-zinc-500">N/A</div>
                        ) : (
                          <>
                            <div className={cn("text-sm font-black", getScoreColorClass(row.operationalScore))}>
                              {row.operationalScore === null ? 'N/A' : pct(row.operationalScore)}
                            </div>
                            {row.operationalScore !== null && (
                              <div className="mt-1 text-[9px] text-zinc-500 uppercase font-black tracking-wider leading-none">
                                {row.kpiCompliance}% × {row.kpiWeight}% + {row.planPct}% × {row.planWeight}% - {row.stalePenaltyApplied}
                              </div>
                            )}
                            {/* Tooltip de Breakdown Matemático Premium */}
                            {row.operationalScore !== null && (
                              <div className="absolute left-1/2 bottom-full -translate-x-1/2 mb-2 w-64 bg-slate-950 border border-slate-800 rounded-xl p-3 shadow-2xl opacity-0 group-hover:opacity-100 pointer-events-none transition-all duration-300 z-50 text-[10px] leading-relaxed normal-case tracking-normal">
                                <div className="font-black text-white border-b border-slate-800 pb-1 uppercase tracking-wider mb-2 text-[9px]">Breakdown del Score</div>
                                <div className="space-y-1">
                                  <div className="flex justify-between">
                                    <span className="text-slate-400">KPI Compliance ({row.kpiWeight}%)</span>
                                    <span className="font-bold text-white">{row.kpiCompliance}% (Contrib: +{Math.round((row.kpiCompliance * row.kpiWeight) / 100)}%)</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-slate-400">Plan Progress ({row.planWeight}%)</span>
                                    <span className="font-bold text-white">{row.planPct}% (Contrib: +{Math.round((row.planPct * row.planWeight) / 100)}%)</span>
                                  </div>
                                  <div className="flex justify-between border-t border-slate-800 pt-1 mt-1">
                                    <span className="text-slate-400">Penalización Stale</span>
                                    <span className="font-bold text-red-400">-{row.stalePenaltyApplied}% ({row.staleMonthsOperational} mes(es))</span>
                                  </div>
                                  <div className="flex justify-between border-t border-slate-800 pt-1 mt-1 font-black">
                                    <span className="text-slate-300">Score de Disciplina</span>
                                    <span className={getScoreColorClass(row.operationalScore)}>{row.operationalScore}%</span>
                                  </div>
                                </div>
                              </div>
                            )}
                          </>
                        )}
                      </td>
                      <td className="px-4 py-4 align-top text-sm font-black text-white">{pct(row.planPct)}</td>
                      <td className="px-4 py-4 align-top text-sm font-black text-white">{pct(row.kpiGoalCoverage)}</td>
                      <td className="px-4 py-4 align-top">
                        <span className={cn('rounded-md border px-2 py-1 text-[10px] font-black uppercase', 
                          row.operationalStatus === 'SIN_OBLIGACION' || row.operationalStatus === 'SIN_CONFIGURACION' || row.operationalStatus === 'SIN_META_VENCIDA'
                            ? 'border-zinc-800 bg-zinc-900/50 text-zinc-500'
                            : row.currentMonthCapture 
                              ? 'border-emerald-500/20 bg-emerald-500/10 text-emerald-300' 
                              : 'border-amber-500/20 bg-amber-500/10 text-amber-300'
                        )}>
                          {row.operationalStatus === 'SIN_OBLIGACION' || row.operationalStatus === 'SIN_CONFIGURACION' || row.operationalStatus === 'SIN_META_VENCIDA' ? 'Exento' : (row.currentMonthCapture ? 'Si' : 'No')}
                        </span>
                      </td>
                      <td className="px-4 py-4 align-top">
                        <div className="text-sm font-black text-white">{displayOperationalMonth(row.operationalStartMonth)}</div>
                        <div className="mt-1 text-[9px] font-black uppercase tracking-widest text-slate-600">{row.operationalStartSource}</div>
                      </td>
                      <td className="px-4 py-4 align-top text-[10px] font-black uppercase tracking-widest text-slate-400">{row.lifecycleSource || 'N/A'}</td>
                      <td className="px-4 py-4 align-top text-[10px] font-black uppercase tracking-widest text-slate-400">{row.onboardingState}</td>
                      <td className="px-4 py-4 align-top text-sm font-black text-white">{row.staleMonthsOperational}</td>
                      <td className="px-4 py-4 align-top">
                        <div className="flex flex-wrap gap-1">
                          {row.alerts.slice(0, 3).map((alert) => (
                            <span key={alert.code} className="rounded border border-slate-700 bg-slate-900 px-1.5 py-1 text-[9px] font-black uppercase tracking-wider text-slate-300">
                              {alert.code}
                            </span>
                          ))}
                          {row.alerts.length === 0 && <span className="text-[10px] font-black uppercase tracking-widest text-slate-600">Sin alertas</span>}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="space-y-3 lg:hidden">
            {filteredRows.map((row) => (
              <InstanceMobileCard 
                key={row.instanceId} 
                row={row} 
                onOpenPlan={() => navigate(buildModulePath('planning', row))}
                onOpenTracking={() => navigate(buildModulePath('tracking', row))}
                onOpenCardex={() => { setSelectedCardexRow(row); setIsCardexOpen(true); }}
              />
            ))}
          </div>

          <aside className="space-y-4">
            <div className="rounded-lg border border-slate-800 bg-slate-950 p-4">
              <div className="flex items-center gap-2">
                <ShieldCheck size={16} className="text-emerald-300" />
                <h2 className="text-xs font-black uppercase tracking-widest text-white">Garantia read-only</h2>
              </div>
              <p className="mt-3 text-xs font-semibold leading-relaxed text-slate-500">
                Este modulo observa servicios existentes y no ejecuta mutaciones, restores, approvals ni recalculos canonicos.
              </p>
            </div>
            
            <div className="rounded-lg border border-slate-800 bg-slate-950 p-4 animate-in fade-in slide-in-from-right duration-500">
              <div className="flex items-center gap-2">
                <Sliders className="text-amber-300" size={16} />
                <h2 className="text-xs font-black uppercase tracking-widest text-white">Semáforo de Disciplina</h2>
              </div>
              <p className="mt-2 text-[9px] font-black text-slate-500 uppercase tracking-widest leading-relaxed">
                Umbrales de Proceso Activos:
              </p>
              <div className="mt-3 space-y-2 text-xs">
                <div className="flex justify-between items-center">
                  <span className="text-slate-400">Óptimo (Verde)</span>
                  <span className="font-black text-emerald-400">≥ {data.opSettings?.greenThreshold}%</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-400">Estable (Amarillo)</span>
                  <span className="font-black text-amber-400">≥ {data.opSettings?.yellowThreshold}%</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-400">Atención (Naranja)</span>
                  <span className="font-black text-orange-400">≥ {data.opSettings?.orangeThreshold}%</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-400">Crítico (Rojo)</span>
                  <span className="font-black text-red-400">&lt; {data.opSettings?.redThreshold}%</span>
                </div>
                <div className="border-t border-slate-800 my-2 pt-2 flex justify-between items-center text-[9px] text-slate-500 uppercase font-black tracking-widest">
                  <span>Rezago Máximo:</span>
                  <span>{data.opSettings?.staleWarningMonths} meses</span>
                </div>
              </div>
            </div>

            <div className="rounded-lg border border-slate-800 bg-slate-950 p-4">
              <div className="flex items-center gap-2">
                <Database size={16} className="text-sky-300" />
                <h2 className="text-xs font-black uppercase tracking-widest text-white">Alertas criticas</h2>
              </div>
              <div className="mt-4 space-y-3">
                {visibleCriticalAlerts.length === 0 && (
                  <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/10 p-3 text-xs font-bold text-emerald-200">
                    Sin alertas criticas visibles en la vista actual.
                  </div>
                )}
                {visibleCriticalAlerts.map((alert) => (
                  <div key={`${alert.instanceId}-${alert.code}`} className="rounded-lg border border-red-500/20 bg-red-500/10 p-3">
                    <div className="text-[10px] font-black uppercase tracking-widest text-red-300">{alert.code}</div>
                    <div className="mt-1 text-xs font-black text-white">{alert.instanceName}</div>
                    <div className="mt-1 text-[11px] font-semibold text-red-100/70">{alert.vpName}</div>
                  </div>
                ))}
              </div>
            </div>
          </aside>
        </section>
        </>
        )}
      </div>

      {/* Cardex Modal */}
      {isCardexOpen && selectedCardexRow && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
          <div className="w-full max-w-5xl bg-slate-900 border border-slate-800 rounded-2xl shadow-2xl flex flex-col h-[80vh] overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between p-4 border-b border-slate-800 bg-slate-950">
              <h2 className="text-sm font-black text-white uppercase tracking-widest">Cardex — {selectedCardexRow.instanceName}</h2>
              <button onClick={() => setIsCardexOpen(false)} className="text-slate-400 hover:text-white p-2 transition-colors">
                <X size={16} />
              </button>
            </div>
            <div className="flex-1 overflow-hidden p-4">
              <AuditCardexPanel defaultSearch={selectedCardexRow.instanceName} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GovernanceOperationalControlCenter;
